import { first, get } from 'lodash';
import { CommonVerbsApiService } from '@services/common/common-verbs-api.service';
import { Component } from '@angular/core';
import { Form } from '@models/forms/form.model';
import { FormBuilder } from '@angular/forms';
import { FormMigRequest } from '@models/account/forms/form-request.model';
import { FormRequestGroup } from '@models/account/forms/form-request.model';
import { FormSectionComponent } from '../form-tools/form-section/form-section.component';
import { FormsDirective } from '../../directives/forms.directive';
import { Inject } from '@angular/core';
import { MigForm } from '@models/forms/mig-form.model';
import { ModelService } from '@services/common/model.service';
import { OnInit } from '@angular/core';
import { Renderer2 } from '@angular/core';
import { StepComponent } from './step.component';
import Swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';
import { ViewChild } from '@angular/core';
import { ViewContainerRef } from '@angular/core';
import { WizardComponent } from 'angular-archwizard';
import { forEach } from 'lodash';

@Component({
  template: '',
  providers: [WizardComponent, CommonVerbsApiService],
})
export abstract class AbstractFormComponent
  extends StepComponent
  implements OnInit
{
  @ViewChild(FormsDirective, { static: true })
  protected formsDirective!: FormsDirective;
  protected container!: ViewContainerRef;
  form: Form;
  sections: any[] = [];
  valid = false;
  formReference: string;

  protected constructor(
    builder: FormBuilder,
    @Inject('MigFormService')
    service: ModelService<MigForm>,
    api: CommonVerbsApiService,
    protected renderer: Renderer2,
    protected toastr: ToastrService,
    @Inject('ID_MIG_FORM') protected idMigForm: string,
    @Inject('formReference') formReference = ''
  ) {
    super(builder, service, api);
    this.formReference = formReference;
  }

  completeName() {
    return this.model?.user
      ? `${this.model.user.firstName} ${this.model.user.lastName}`
      : '';
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.getForm();
  }

  getForm(): void {
    const subscribe = this.api
      .get<Form>('forms/form-by-id/' + this.idMigForm)
      .subscribe(response => {
        this.form = response;
        this.container = this.formsDirective.viewContainerRef;
        this.render();
      });
    this.unsubscribe.push(subscribe);
  }

  private render() {
    this.container.clear();
    forEach(this.form.formFieldset, section => {
      const component =
        this.container.createComponent<FormSectionComponent>(
          FormSectionComponent
        );
      component.instance.section = section;
      component.instance.section.formReference = this.formReference;

      if (
        section.name === 'SHORT_BIOGRAPHY' ||
        section.name === 'PREVIOUS_LIVING_SITUATION' ||
        section.name === 'REASONS_FOR_MOVING'
      ) {
        this.renderer.setStyle(
          component.location.nativeElement,
          'width',
          '50%'
        );
      }
      this.sections.push(component);
    });
  }

  ngSubmit() {
    Swal.fire({
      title: 'Are you sure?',
      text: `This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#07B59A',
      cancelButtonColor: '#f46a6a',
      confirmButtonText: 'Confirm',
    }).then(result => {
      if (result.value) {
        const params = this.params;
        if (this.valid) {
          const subscribe = this.api
            .post(`users/${this.model?.user?.id}/responses-save`, params)
            .subscribe(response => {
              this.nextStep.emit();
            });
          this.unsubscribe.push(subscribe);
        }
      }
    });
  }

  get params() {
    let invalidadFormFlagAux = false;

    const request: FormMigRequest = {
      formId: this.form.id,
      fillerName: this.model?.name ?? '',
      fillerEmail: this.model?.email ?? '',
      formFieldsetGroup: [],
      checkMigForm: true,
    };

    forEach(this.sections, s => {
      const section = s.instance;
      const groups = section.componentGroups;
      forEach(groups, g => {
        const groupInstance = g.instance;
        const group = groupInstance.group;
        if (!group.isContinuous) {
          const formFieldsetGroup: FormRequestGroup = {
            id: group.id,
            responses: [],
          };
          const fields = groupInstance.fields;
          forEach(fields, f => {
            const fieldInstance = f.instance;
            const question = fieldInstance.question;
            const params = question.params
              ? first(JSON.parse(question.params))
              : [];
            const required = get(params, 'required', false);
            const options = get(first(question.options), 'jsonValue', {});

            let value = fieldInstance.form.field.value ?? '';
            if (question.questionType === 'FILE') {
              value = fieldInstance.mediaId ?? '';
            }
            if (question.questionType === 'BOOLEAN') {
              value = value ? 'yes' : 'no';
            }
            if (required && !value) {
              invalidadFormFlagAux = true;
              this.toastr.error(`Fill ${question.label} required field.`);
              return;
            }

            if (value instanceof Array) {
              forEach(value, v => {
                if (question.questionType === 'CHECKBOX') {
                  v = get(options, v, v);
                }
                formFieldsetGroup.responses.push({
                  questionId: question.id,
                  value: v,
                });
              });
            } else {
              formFieldsetGroup.responses.push({
                questionId: question.id,
                value: value,
              });
            }
          });
          request.formFieldsetGroup.push([formFieldsetGroup]);
        } else {
          const blocks = section.familyRelativesAddded;
          const merge: FormRequestGroup[] = [];
          forEach(blocks, b => {
            const formFieldsetGroup: FormRequestGroup = {
              id: group.id,
              responses: [],
            };
            forEach(group.questions, q => {
              let value = get(b, `${q.reference}`.toLowerCase())?.value ?? '';
              const options = get(first(q.options), 'jsonValue', {});
              if (value instanceof Array) {
                forEach(value, v => {
                  if (q.questionType === 'BOOLEAN') {
                    v = v === 'true' ? 'yes' : 'no';
                  }
                  if (q.questionType === 'CHECKBOX') {
                    v = get(options, v, v);
                  }
                  formFieldsetGroup.responses.push({
                    questionId: q.id,
                    value: v,
                  });
                });
              } else {
                if (q.questionType === 'BOOLEAN') {
                  value = value === 'true' ? 'yes' : 'no';
                }
                formFieldsetGroup.responses.push({
                  questionId: q.id,
                  value: value,
                });
              }
            });
            merge.push(formFieldsetGroup);
          });
          if (merge.length) {
            request.formFieldsetGroup.push(merge);
          }
        }
      });
    });
    this.valid = !invalidadFormFlagAux;
    return request;
  }
}
